import type {
  CreateWikiCommentBody,
  CreateWikiPageBody,
  MoveWikiPageBody,
  UpdateWikiPageBody,
  WikiAttachment,
  WikiComment,
  WikiDiff,
  WikiImportPlan,
  WikiPage,
  WikiPageSummary,
  WikiPageVersion,
  WikiSearchResult,
  WikiTemplate,
  WikiTransfer,
} from '@tasks/contracts';

import {
  apiRequest,
  apiRequestList,
  apiRequestResource,
  apiRequestVoid,
} from './client';

/**
 * Wiki проекта (docs/14).
 *
 * Wiki независима от git: страницы живут в БД, а обмен с внешним миром идёт
 * импортом и экспортом markdown-архивов администратором проекта.
 */

function base(key: string): string {
  return `/projects/${encodeURIComponent(key)}/wiki`;
}

export function listPages(key: string, signal?: AbortSignal): Promise<WikiPageSummary[]> {
  return apiRequestList<WikiPageSummary>(`${base(key)}/pages`, { signal });
}

export function getPage(key: string, pageId: string, signal?: AbortSignal): Promise<WikiPage> {
  return apiRequestResource<WikiPage>(
    `${base(key)}/pages/${encodeURIComponent(pageId)}`,
    'page',
    { signal },
  );
}

export function createPage(key: string, body: CreateWikiPageBody): Promise<WikiPage> {
  return apiRequestResource<WikiPage>(`${base(key)}/pages`, 'page', {
    method: 'POST',
    body,
  });
}

export function updatePage(
  key: string,
  pageId: string,
  body: UpdateWikiPageBody,
): Promise<WikiPage> {
  return apiRequestResource<WikiPage>(
    `${base(key)}/pages/${encodeURIComponent(pageId)}`,
    'page',
    { method: 'PATCH', body },
  );
}

export function movePage(
  key: string,
  pageId: string,
  body: MoveWikiPageBody,
): Promise<WikiPage> {
  return apiRequestResource<WikiPage>(
    `${base(key)}/pages/${encodeURIComponent(pageId)}/move`,
    'page',
    { method: 'PATCH', body },
  );
}

export function deletePage(key: string, pageId: string): Promise<void> {
  return apiRequestVoid(`${base(key)}/pages/${encodeURIComponent(pageId)}`, {
    method: 'DELETE',
  });
}

export function listTemplates(
  key: string,
  signal?: AbortSignal,
): Promise<WikiTemplate[]> {
  return apiRequestList<WikiTemplate>(`${base(key)}/templates`, { signal });
}

export function createTemplate(
  key: string,
  body: { name: string; body: string },
): Promise<WikiTemplate> {
  return apiRequestResource<WikiTemplate>(`${base(key)}/templates`, 'template', {
    method: 'POST',
    body,
  });
}

export function updateTemplate(
  key: string,
  templateId: string,
  body: { name?: string; body?: string },
): Promise<WikiTemplate> {
  return apiRequestResource<WikiTemplate>(
    `${base(key)}/templates/${encodeURIComponent(templateId)}`,
    'template',
    { method: 'PATCH', body },
  );
}

export function deleteTemplate(key: string, templateId: string): Promise<void> {
  return apiRequestVoid(`${base(key)}/templates/${encodeURIComponent(templateId)}`, {
    method: 'DELETE',
  });
}

export function listVersions(
  key: string,
  pageId: string,
  signal?: AbortSignal,
): Promise<WikiPageVersion[]> {
  return apiRequestList<WikiPageVersion>(
    `${base(key)}/pages/${encodeURIComponent(pageId)}/versions`,
    { signal },
  );
}

export async function diffVersions(
  key: string,
  pageId: string,
  from: number,
  to: number,
): Promise<WikiDiff> {
  const payload = await apiRequest<{ diff: WikiDiff }>(
    `${base(key)}/pages/${encodeURIComponent(pageId)}/diff?from=${from}&to=${to}`,
  );
  return payload.diff;
}

export function revertPage(key: string, pageId: string, version: number): Promise<WikiPage> {
  return apiRequestResource<WikiPage>(
    `${base(key)}/pages/${encodeURIComponent(pageId)}/revert`,
    'page',
    { method: 'POST', body: { version } },
  );
}

export function searchPages(
  key: string,
  query: string,
  signal?: AbortSignal,
): Promise<WikiSearchResult[]> {
  return apiRequestList<WikiSearchResult>(
    `${base(key)}/search?q=${encodeURIComponent(query)}`,
    { signal },
  );
}

// --- Комментарии и вложения страницы ---

export function listComments(
  key: string,
  pageId: string,
  signal?: AbortSignal,
): Promise<WikiComment[]> {
  return apiRequestList<WikiComment>(
    `${base(key)}/pages/${encodeURIComponent(pageId)}/comments`,
    { signal },
  );
}

export function createComment(
  key: string,
  pageId: string,
  body: CreateWikiCommentBody,
): Promise<WikiComment> {
  return apiRequestResource<WikiComment>(
    `${base(key)}/pages/${encodeURIComponent(pageId)}/comments`,
    'comment',
    { method: 'POST', body },
  );
}

export function listAttachments(
  key: string,
  pageId: string,
  signal?: AbortSignal,
): Promise<WikiAttachment[]> {
  return apiRequestList<WikiAttachment>(
    `${base(key)}/pages/${encodeURIComponent(pageId)}/attachments`,
    { signal },
  );
}

export function deleteAttachment(attachmentId: string): Promise<void> {
  return apiRequestVoid(`/wiki/attachments/${encodeURIComponent(attachmentId)}`, {
    method: 'DELETE',
  });
}

export function downloadAttachmentUrl(attachmentId: string): string {
  return `/api/wiki/attachments/${encodeURIComponent(attachmentId)}`;
}

// --- Обмен архивами (docs/14 §5) ---

export interface ImportOptions {
  readonly dryRun: boolean;
  readonly force: boolean;
  readonly parentId?: string | null;
}

/** Импорт: флаги идут в query, файл — в multipart (см. роут). */
export async function importArchive(
  key: string,
  file: File,
  options: ImportOptions,
): Promise<{ dryRun: boolean; plan: WikiImportPlan; transfer: WikiTransfer | null }> {
  const form = new FormData();
  form.append('file', file);

  const query = new URLSearchParams({
    dryRun: String(options.dryRun),
    force: String(options.force),
  });
  if (options.parentId) query.set('parentId', options.parentId);

  return apiRequest(`${base(key)}/import?${query.toString()}`, {
    method: 'POST',
    formData: form,
  });
}

/** Скачивание архива: сервер отдаёт ZIP, клиент сохраняет его файлом. */
export async function downloadArchive(key: string, pageId?: string): Promise<void> {
  const query = pageId ? `?pageId=${encodeURIComponent(pageId)}` : '';
  const response = await fetch(`${base(key)}/export${query}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Не удалось выгрузить архив');
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `wiki-${key}.zip`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function listTransfers(
  key: string,
  signal?: AbortSignal,
): Promise<WikiTransfer[]> {
  return apiRequestList<WikiTransfer>(`${base(key)}/transfers`, { signal });
}
